/**
 * Keys the SSO round-trip parks in `sessionStorage`.
 *
 * They live here rather than in `auth.guard.ts` because both the guard and
 * `AuthService` need them, and the guard now reports its decision back into the
 * service — importing them from the guard would close an `auth.service` →
 * `auth.guard` → `auth.service` cycle.
 */

/**
 * Guards against an infinite login loop: if the token exchange keeps failing,
 * `isAuthenticated` stays false and we would redirect to Ping on every return.
 * At most one redirect is allowed per attempt.
 */
export const REDIRECT_FLAG = 'sso_redirect_attempted';

/**
 * Ping's redirectUrl is the SPA root, so a deep-link like /mercury-form/:id is
 * lost across the SSO round-trip. The guard stashes the target here before
 * `authorize()`; `AuthService.refresh()` restores it once the auth check
 * succeeds.
 */
export const SSO_TARGET_URL_KEY = 'sso_target_url';

/**
 * The single localStorage entry angular-auth-oidc-client keeps everything in.
 *
 * Set explicitly rather than left to the library, which would derive
 * `${index}-${clientId}`. `AuthService` has to reach into this entry directly to
 * clear a stale flow flag (see `clearStaleCodeFlowFlag`), and deriving the same
 * string in two places would break silently on a library upgrade.
 */
export const PING_CONFIG_ID = 'panorama';

/**
 * Key *inside* the `PING_CONFIG_ID` blob. The library sets it in `authorize()`
 * and clears it only on a callback — there is no staleness timeout, unlike
 * `storageSilentRenewRunning`. Named here because `AuthService` has to clear it.
 */
export const OIDC_CODE_FLOW_IN_PROGRESS = 'storageCodeFlowInProgress';

/**
 * Where FILM parks the route a user asked for before handing them to Ping.
 *
 * FILM (`/film`, a separate SPA) shares this app's Ping client and its
 * `PING_CONFIG_ID` storage entry, and Ping returns every login to the domain
 * root — which is this app. So a cold visit to `/film/form-manager` comes back
 * here with `?code=`, and this app completes the exchange (the PKCE verifier
 * FILM stored sits in the shared entry). `AuthService` then reads this key and
 * hands the user back to `FILM_BASE_PATH` + the value.
 *
 * Router-relative to FILM (`/form-manager?tab=all`). Older FILM builds stored
 * the full pathname (`/film/form-manager`); both are accepted. Must stay
 * identical to `POST_LOGIN_URL` in FILM's core/constants/auth.constants.ts.
 */
export const FILM_POST_LOGIN_URL_KEY = 'film_post_login_url';

/** FILM's base href on the shared hostname. */
export const FILM_BASE_PATH = '/film';
